import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';

const CustomerCreditAccounts = () => {
    const { authFetch } = useContext(AppContext);
    const { t } = useTranslation();
    const [customers, setCustomers] = useState([]);
    
    // Modals
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isStatementModalOpen, setIsStatementModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerDetails, setCustomerDetails] = useState(null); // Full ledger details
    const [form, setForm] = useState({ name: '', phone: '', address: '', credit_limit: '' });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Pay Invoice State
    const [isPayInvoiceModalOpen, setIsPayInvoiceModalOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [payAmount, setPayAmount] = useState('');
    const [payMethod, setPayMethod] = useState('Cash');

    // Filters State
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterMinAmt, setFilterMinAmt] = useState('');
    const [filterMaxAmt, setFilterMaxAmt] = useState('');

    // Pay All State
    const [isPayAllModalOpen, setIsPayAllModalOpen] = useState(false);
    const [payAllAmount, setPayAllAmount] = useState('');
    const [payAllMethod, setPayAllMethod] = useState('Cash');

    const fetchCustomers = () => {
        setLoading(true);
        setError('');
        authFetch('/api/customers')
            .then(r => r.json())
            .then(d => setCustomers(Array.isArray(d) ? d : []))
            .catch(() => setError(t('Failed to load customers')))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchCustomers(); }, []);

    const fetchCustomerStatement = async (customerId) => {
        try {
            const res = await authFetch(`/api/customers/${customerId}`);
            if (res.ok) {
                const data = await res.json();
                setCustomerDetails(data);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleViewStatement = (customer) => {
        setSelectedCustomer(customer);
        fetchCustomerStatement(customer.id);
        setIsStatementModalOpen(true);
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const res = await authFetch('/api/customers', { method: 'POST', body: JSON.stringify(form) });
            if (!res.ok) throw new Error(t('Failed to add customer'));
            setSuccess(t('Customer added successfully'));
            setIsAddModalOpen(false);
            setForm({ name: '', phone: '', address: '', credit_limit: '' });
            fetchCustomers();
        } catch (err) {
            setError(t('Failed to add customer'));
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        try {
            const res = await authFetch(`/api/customers/${selectedCustomer.id}`, {
                method: 'PUT',
                body: JSON.stringify(form)
            });
            if (res.ok) {
                setIsEditModalOpen(false);
                fetchCustomers();
            }
        } catch (err) {
            alert(t('Update failed.'));
        }
    };

    const handleDelete = async () => {
        try {
            const res = await authFetch(`/api/customers/${selectedCustomer.id}`, { method: 'DELETE' });
            if (res.ok) {
                setIsDeleteModalOpen(false);
                fetchCustomers();
            }
        } catch (err) {
            alert(t('Delete failed.'));
        }
    };

    // Pay specific invoice
    const handlePayInvoice = async (e) => {
        e.preventDefault();
        const amount = parseFloat(payAmount);
        if (!amount || amount <= 0) return alert(t('Enter a valid amount.'));
        
        try {
            const res = await authFetch(`/api/invoices?action=pay&id=${selectedInvoice.id}`, {
                method: 'POST',
                body: JSON.stringify({ amount, note: `Paid via ${payMethod}` })
            });
            if (res.ok) {
                setIsPayInvoiceModalOpen(false);
                setPayAmount('');
                // Refresh both the customer list and the statement modal
                fetchCustomers();
                if (selectedCustomer) fetchCustomerStatement(selectedCustomer.id);
            } else {
                const err = await res.json();
                alert(err.message || t('Payment failed'));
            }
        } catch (err) {
            alert(t('Network error while paying.'));
        }
    };

    const handlePayAll = async (e) => {
        e.preventDefault();
        const amount = parseFloat(payAllAmount);
        if (!amount || amount <= 0) return alert(t('Enter a valid amount.'));
        
        try {
            const res = await authFetch('/api/customer_payments', {
                method: 'POST',
                body: JSON.stringify({ customer_id: selectedCustomer.id, amount, payment_method: payAllMethod, note: `Bulk payoff via ${payAllMethod}` })
            });
            if (res.ok) {
                setIsPayAllModalOpen(false);
                setPayAllAmount('');
                fetchCustomers();
                if (selectedCustomer) fetchCustomerStatement(selectedCustomer.id);
            } else {
                const err = await res.json();
                alert(err.message || t('Payment failed'));
            }
        } catch (err) {
            alert(t('Network error while paying.'));
        }
    };

    const filteredInvoices = (customerDetails?.invoices || []).filter(inv => {
        const d = new Date(inv.created_at);
        const amt = parseFloat(inv.total_amount || 0);
        
        if (filterStartDate && d < new Date(filterStartDate)) return false;
        if (filterEndDate) {
            const end = new Date(filterEndDate);
            end.setHours(23, 59, 59, 999);
            if (d > end) return false;
        }
        if (filterMinAmt && amt < parseFloat(filterMinAmt)) return false;
        if (filterMaxAmt && amt > parseFloat(filterMaxAmt)) return false;
        return true;
    });

    const totalAR = customers.reduce((s, c) => s + parseFloat(c.outstanding_balance || 0), 0);

    const statusColor = (balance) => {
        if (balance <= 0) return 'text-emerald-600';
        if (balance < 500) return 'text-orange-500';
        return 'text-red-600 font-bold';
    };

    const columns = [
        { header: t('Customer'), accessor: 'name', render: r => <span className="font-semibold">{r.name}</span> },
        { header: t('Phone'), accessor: 'phone', render: r => r.phone || '—' },
        { header: t('Total Invoiced'), accessor: 'total_invoiced', render: r => `CFA${parseFloat(r.total_invoiced).toFixed(2)}` },
        { header: t('Total Paid'), accessor: 'total_paid', render: r => <span className="text-emerald-700">CFA{parseFloat(r.total_paid).toFixed(2)}</span> },
        {
            header: t('Outstanding Balance'), accessor: 'outstanding_balance', render: r => (
                <span className={statusColor(parseFloat(r.outstanding_balance))}>
                    CFA{parseFloat(r.outstanding_balance).toFixed(2)}
                </span>
            )
        },
        {
            header: t('Actions'),
            accessor: 'actions',
            render: (row) => (
                <div className="flex gap-2">
                    <button className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-semibold" onClick={() => handleViewStatement(row)}>
                        {t('Statement')}
                    </button>
                    <button className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200" onClick={() => { setSelectedCustomer(row); setForm({ name: row.name, phone: row.phone, address: row.address, credit_limit: row.credit_limit }); setIsEditModalOpen(true); }}>
                        {t('Edit')}
                    </button>
                    <button className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200" onClick={() => { setSelectedCustomer(row); setIsDeleteModalOpen(true); }}>
                        {t('Delete')}
                    </button>
                </div>
            )
        },
    ];

    return (
        <div className="space-y-6">
            <header className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">{t('Customer Accounts')}</h2>
                    <p className="text-gray-500 text-sm mt-1">
                        {t('Total Accounts Receivable:')} <span className="font-bold text-red-600">CFA{totalAR.toFixed(2)}</span>
                    </p>
                </div>
                <button onClick={() => setIsAddModalOpen(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700 transition">
                    {t('+ New Customer')}
                </button>
            </header>

            {error && <div className="bg-red-100 text-red-700 p-2 rounded">{error}</div>}
            {success && <div className="bg-emerald-100 text-emerald-700 p-2 rounded">{success}</div>}
            
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <DataTable columns={columns} data={customers} />
            </div>

            {/* Statement Modal */}
            <Modal isOpen={isStatementModalOpen} onClose={() => setIsStatementModalOpen(false)} title={
                <div className="flex justify-between items-center w-full pr-8">
                    <span>{t('Customer Statement & Ledger')}</span>
                    {customerDetails && parseFloat(customerDetails.outstanding_balance) > 0 && (
                        <button 
                            onClick={() => {
                                setPayAllAmount(parseFloat(customerDetails.outstanding_balance).toFixed(2));
                                setIsPayAllModalOpen(true);
                            }} 
                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-1.5 rounded-lg shadow font-bold transition">
                            {t('Pay Off Outstanding')}
                        </button>
                    )}
                </div>
            } size="5xl">
                {customerDetails ? (
                    <div className="space-y-6">
                        {/* Customer Header Info */}
                        <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div>
                                <h3 className="font-bold text-lg text-gray-800">{customerDetails.name}</h3>
                                <p className="text-gray-600 text-sm">{customerDetails.phone || t('No Phone')}</p>
                                <p className="text-gray-600 text-sm">{customerDetails.address || t('No Address')}</p>
                            </div>
                            <div className="text-right space-y-1">
                                <div className="flex justify-end gap-2"><span className="text-gray-500">{t('Total Invoiced')}:</span> <span className="font-bold text-gray-800">CFA{parseFloat(customerDetails.total_invoiced).toFixed(2)}</span></div>
                                <div className="flex justify-end gap-2"><span className="text-gray-500">{t('Total Paid')}:</span> <span className="font-bold text-emerald-600">CFA{parseFloat(customerDetails.total_paid).toFixed(2)}</span></div>
                                <div className="flex justify-end gap-2 border-t pt-1 mt-1"><span className="text-gray-500 font-bold">{t('Balance')}:</span> <span className={`font-bold ${parseFloat(customerDetails.outstanding_balance) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>CFA{parseFloat(customerDetails.outstanding_balance).toFixed(2)}</span></div>
                            </div>
                        </div>

                        {/* Filters */}
                        <div className="grid grid-cols-4 gap-3 bg-gray-50 p-3 rounded-lg border border-gray-200 text-sm">
                            <div>
                                <label className="block text-gray-500 font-semibold mb-1 text-xs">{t('Start Date')}</label>
                                <input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} className="w-full border rounded p-1.5" />
                            </div>
                            <div>
                                <label className="block text-gray-500 font-semibold mb-1 text-xs">{t('End Date')}</label>
                                <input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} className="w-full border rounded p-1.5" />
                            </div>
                            <div>
                                <label className="block text-gray-500 font-semibold mb-1 text-xs">{t('Min Amount')}</label>
                                <input type="number" placeholder="0.00" value={filterMinAmt} onChange={e => setFilterMinAmt(e.target.value)} className="w-full border rounded p-1.5" />
                            </div>
                            <div>
                                <label className="block text-gray-500 font-semibold mb-1 text-xs">{t('Max Amount')}</label>
                                <input type="number" placeholder="9999.00" value={filterMaxAmt} onChange={e => setFilterMaxAmt(e.target.value)} className="w-full border rounded p-1.5" />
                            </div>
                        </div>

                        {/* Invoices Ledger Table */}
                        <div className="overflow-x-auto border rounded-lg">
                            <table className="w-full text-sm text-left text-gray-600">
                                <thead className="bg-gray-100 text-gray-800 font-semibold uppercase text-xs">
                                    <tr>
                                        <th className="px-4 py-3">{t('Date')}</th>
                                        <th className="px-4 py-3">{t('Invoice No')}</th>
                                        <th className="px-4 py-3">{t('Type')}</th>
                                        <th className="px-4 py-3 text-right">{t('Amount')}</th>
                                        <th className="px-4 py-3 text-right">{t('Paid')}</th>
                                        <th className="px-4 py-3 text-center">{t('Status')}</th>
                                        <th className="px-4 py-3 text-center">{t('Actions')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredInvoices.length > 0 ? (
                                        filteredInvoices.map((inv, idx) => {
                                            const amount = parseFloat(inv.total_amount || 0);
                                            const paid = parseFloat(inv.amount_paid || 0);
                                            const isUnpaid = inv.status === 'Unpaid' || inv.status === 'Partial';
                                            return (
                                                <tr key={idx} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3">{new Date(inv.created_at).toLocaleString()}</td>
                                                    <td className="px-4 py-3 font-mono">{inv.invoice_number}</td>
                                                    <td className="px-4 py-3">
                                                        <span className="bg-gray-200 px-2 py-0.5 rounded text-xs">{inv.type}</span>
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-semibold">CFA{amount.toFixed(2)}</td>
                                                    <td className="px-4 py-3 text-right text-emerald-600">CFA{paid.toFixed(2)}</td>
                                                    <td className="px-4 py-3 text-center">
                                                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                                                            inv.status === 'Paid' ? 'bg-emerald-100 text-emerald-700' :
                                                            inv.status === 'Partial' ? 'bg-orange-100 text-orange-700' :
                                                            'bg-red-100 text-red-700'
                                                        }`}>
                                                            {inv.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        {isUnpaid && (
                                                            <button 
                                                                onClick={() => {
                                                                    setSelectedInvoice(inv);
                                                                    setPayAmount((amount - paid).toFixed(2));
                                                                    setIsPayInvoiceModalOpen(true);
                                                                }}
                                                                className="bg-indigo-100 text-indigo-700 hover:bg-indigo-200 px-3 py-1 rounded text-xs font-bold transition-colors">
                                                                {t('Pay')}
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">{t('No transactions found.')}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="flex justify-center p-10"><span className="animate-spin text-4xl">⏳</span></div>
                )}
            </Modal>

            {/* Pay Specific Invoice Modal */}
            <Modal isOpen={isPayInvoiceModalOpen} onClose={() => setIsPayInvoiceModalOpen(false)} title={t('Pay Invoice')}>
                {selectedInvoice && (
                    <form onSubmit={handlePayInvoice} className="space-y-4">
                        <div className="bg-gray-50 p-3 rounded text-sm text-gray-700">
                            {t('Invoice')}: <strong>{selectedInvoice.invoice_number}</strong><br/>
                            {t('Total Amount')}: <strong>CFA {parseFloat(selectedInvoice.total_amount).toFixed(2)}</strong><br/>
                            {t('Amount Paid')}: <strong>CFA {parseFloat(selectedInvoice.amount_paid).toFixed(2)}</strong><br/>
                            {t('Remaining Balance')}: <strong className="text-red-600">CFA {(parseFloat(selectedInvoice.total_amount) - parseFloat(selectedInvoice.amount_paid)).toFixed(2)}</strong>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold mb-1">{t('Payment Method')}</label>
                            <select value={payMethod} onChange={e => setPayMethod(e.target.value)} className="w-full border p-2 rounded" required>
                                <option value="Cash">Cash</option>
                                <option value="Visa">Visa / Card</option>
                                <option value="Bank Transfer">Bank Transfer</option>
                                <option value="Mobile Money">Mobile Money</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold mb-1">{t('Amount to Pay')}</label>
                            <input type="number" step="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)} className="w-full border p-2 rounded text-lg font-bold" required />
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <button type="button" onClick={() => setIsPayInvoiceModalOpen(false)} className="px-4 py-2 border rounded text-gray-600">{t('Cancel')}</button>
                            <button type="submit" className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 font-bold shadow">{t('Submit Payment')}</button>
                        </div>
                    </form>
                )}
            </Modal>

            {/* Pay All Outstanding Modal */}
            <Modal isOpen={isPayAllModalOpen} onClose={() => setIsPayAllModalOpen(false)} title={t('Pay Off Outstanding Balance')}>
                {selectedCustomer && (
                    <form onSubmit={handlePayAll} className="space-y-4">
                        <div className="bg-emerald-50 text-emerald-800 p-4 rounded-lg border border-emerald-100">
                            <p className="text-sm">{t('Customer')}: <strong className="text-lg">{selectedCustomer.name}</strong></p>
                            <p className="text-sm mt-1">{t('Total Outstanding')}: <strong className="text-2xl">CFA {parseFloat(customerDetails?.outstanding_balance || 0).toFixed(2)}</strong></p>
                            <p className="text-xs mt-2 text-emerald-600">{t('This will apply the payment to the oldest unpaid invoices first.')}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold mb-1">{t('Payment Method')}</label>
                            <select value={payAllMethod} onChange={e => setPayAllMethod(e.target.value)} className="w-full border p-2 rounded" required>
                                <option value="Cash">Cash</option>
                                <option value="Visa">Visa / Card</option>
                                <option value="Bank Transfer">Bank Transfer</option>
                                <option value="Mobile Money">Mobile Money</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold mb-1">{t('Amount to Pay')}</label>
                            <input type="number" step="0.01" value={payAllAmount} onChange={e => setPayAllAmount(e.target.value)} className="w-full border p-2 rounded text-lg font-bold" required />
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <button type="button" onClick={() => setIsPayAllModalOpen(false)} className="px-4 py-2 border rounded text-gray-600">{t('Cancel')}</button>
                            <button type="submit" className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 font-bold shadow">{t('Submit Bulk Payment')}</button>
                        </div>
                    </form>
                )}
            </Modal>

            {/* Add Modal */}
            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title={t('Add Customer')}>
                <form onSubmit={handleAdd} className="space-y-3">
                    <input type="text" placeholder={t('Name')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border p-2 rounded" required />
                    <input type="text" placeholder={t('Phone')} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full border p-2 rounded" />
                    <input type="text" placeholder={t('Address')} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="w-full border p-2 rounded" />
                    <input type="number" placeholder={t('Credit Limit')} value={form.credit_limit} onChange={e => setForm({ ...form, credit_limit: e.target.value })} className="w-full border p-2 rounded" />
                    <div className="flex justify-end pt-2">
                        <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded">{t('Save')}</button>
                    </div>
                </form>
            </Modal>

            {/* Edit Modal */}
            <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title={t('Edit Customer')}>
                <form onSubmit={handleEdit} className="space-y-3">
                    <input type="text" placeholder={t('Name')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border p-2 rounded" required />
                    <input type="text" placeholder={t('Phone')} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full border p-2 rounded" />
                    <input type="text" placeholder={t('Address')} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="w-full border p-2 rounded" />
                    <input type="number" placeholder={t('Credit Limit')} value={form.credit_limit} onChange={e => setForm({ ...form, credit_limit: e.target.value })} className="w-full border p-2 rounded" />
                    <div className="flex justify-end pt-2">
                        <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded">{t('Update')}</button>
                    </div>
                </form>
            </Modal>

            {/* Delete Modal */}
            <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title={t('Confirm Deletion')}>
                <p>{t('Are you sure you want to delete this customer?')}</p>
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={() => setIsDeleteModalOpen(false)} className="px-4 py-2 border rounded">{t('Cancel')}</button>
                    <button onClick={handleDelete} className="bg-red-600 text-white px-4 py-2 rounded">{t('Delete')}</button>
                </div>
            </Modal>
        </div>
    );
};

export default CustomerCreditAccounts;
